import { div, h1, h2, p, span, button, img, textarea, fnstate } from '@srfnstack/fntags'

// Blind labelling for the quality gate.
//
// The gate has never been measured against anything, so no rubric change can be
// called an improvement. This is where the yardstick gets made: one piece of
// artwork at a time, as large as the window allows, with NO score, verdict or
// reason anywhere on the screen. A reviewer shown "score 9.2" is anchored by it
// and the resulting labels measure the model against itself.
//
// The queue is stratified server-side and includes the gate's auto-rejects,
// which appear on no other screen — a gate that throws away good work is
// invisible until someone looks at what it threw away.
export const GateLabeling = () => {
  const items = fnstate([])
  const idx = fnstate(0)
  const kinds = fnstate([])
  const selected = fnstate([])
  const note = fnstate('')
  const loading = fnstate(true)
  const saving = fnstate(false)
  const labelled = fnstate(0)
  const type = fnstate('IMAGE')
  const stats = fnstate(null)
  // null = every product type. Otherwise the list the queue scoped itself to,
  // which by default is whatever autopilot is currently set to generate.
  const scope = fnstate(null)
  const allTypes = fnstate(false)

  const current = () => items()[idx()] || null

  const load = async () => {
    loading(true)
    idx(0)
    selected([])
    note('')
    try {
      const scopeParam = allTypes() ? '&product_type=all' : ''
      const [qRes, kRes] = await Promise.all([
        fetch(`/api/system/autopilot/labels/queue?type=${type()}${scopeParam}`),
        fetch(`/api/system/autopilot/labels/defect-kinds?type=${type()}`)
      ])
      const q = qRes.ok ? await qRes.json() : { items: [], labelled: 0, product_types: null }
      items(q.items || [])
      labelled(q.labelled || 0)
      scope(q.product_types || null)
      kinds(kRes.ok ? await kRes.json() : [])
    } catch {
      items([])
      kinds([])
    }
    loading(false)
  }

  const loadStats = async () => {
    try {
      const r = await fetch(`/api/system/autopilot/labels?type=${type()}`)
      stats(r.ok ? await r.json() : null)
    } catch { stats(null) }
  }

  const toggleDefect = (kind) => {
    const cur = selected()
    selected(cur.includes(kind) ? cur.filter(k => k !== kind) : [...cur, kind])
  }

  const save = async (verdict) => {
    const item = current()
    if (!item || saving()) return
    saving(true)
    try {
      const res = await fetch('/api/system/autopilot/labels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: item.id, verdict, defects: selected(), note: note() })
      })
      if (res.ok) {
        labelled(labelled() + 1)
        selected([])
        note('')
        idx(idx() + 1)
      } else {
        const e = await res.json().catch(() => ({}))
        await window.alert(`Failed to save label: ${e.error || 'error'}`)
      }
    } catch (err) {
      await window.alert(`Failed to save label: ${err.message}`)
    }
    saving(false)
  }

  const switchType = async (t) => {
    type(t)
    stats(null)
    await load()
  }

  // Keyboard: labelling 150 images with a mouse is how reviewer fatigue gets
  // into the calibration set.
  const onKey = (e) => {
    if (e.target && ['TEXTAREA', 'INPUT'].includes(e.target.tagName)) return
    if (e.key === 's' || e.key === 'S') { e.preventDefault(); save('ship') }
    if (e.key === 'r' || e.key === 'R') { e.preventDefault(); save('reject') }
    if (e.key === 'ArrowLeft' && idx() > 0) idx(idx() - 1)
    if (e.key === 'ArrowRight' && idx() < items().length - 1) idx(idx() + 1)
  }

  load()
  window.addEventListener('keydown', onKey)

  const typeTab = (t, label) => button({
    class: type.bindAttr(cur => `tab ${cur === t ? 'tab-active' : ''}`),
    onclick: () => switchType(t)
  }, label)

  const defectChips = () => kinds.bindChildren(
    div({ class: 'flex flex-wrap gap-2' }),
    (kState) => {
      const k = kState()
      return selected.bindAs(sel => button({
        class: `badge badge-lg cursor-pointer ${sel.includes(k.kind) ? 'badge-error' : 'badge-ghost'}`,
        title: k.description,
        onclick: () => toggleDefect(k.kind)
      }, k.label))
    }
  )

  const reviewer = () => div({ class: 'space-y-3' },
    div({ class: 'flex items-center gap-3 text-sm' },
      span({ class: 'badge badge-neutral' }, idx.bindAs(i => `${i + 1} / ${items().length} in this batch`)),
      span({ class: 'opacity-70' }, labelled.bindAs(n => `${n} labelled so far`))
    ),
    div({ class: 'bg-base-300 rounded-lg p-2' },
      img({
        src: idx.bindAttr(() => {
          const c = current()
          return c ? `/api/image?file=${encodeURIComponent(c.file_path)}` : ''
        }),
        class: 'w-full rounded bg-base-100',
        style: 'height: 58vh; object-fit: contain;',
        alt: 'artwork under review'
      })
    ),
    div({ class: 'card bg-base-200 border border-base-300 p-4 space-y-3' },
      div({},
        p({ class: 'text-xs uppercase tracking-wide opacity-60 mb-2' }, "What's wrong with it? (optional)"),
        defectChips()
      ),
      textarea({
        class: 'textarea textarea-bordered w-full',
        rows: 2,
        placeholder: 'Anything the chips do not cover…',
        value: note.bindAttr(n => n),
        oninput: (e) => note(e.target.value)
      }),
      div({ class: 'flex gap-3 flex-wrap items-center' },
        button({
          class: 'btn btn-success',
          disabled: saving.bindAttr(s => s),
          onclick: () => save('ship')
        }, 'Ship it (S)'),
        button({
          class: 'btn btn-error',
          disabled: saving.bindAttr(s => s),
          onclick: () => save('reject')
        }, 'Reject (R)'),
        span({ class: 'text-xs opacity-60 ml-auto' }, 'No score is shown on purpose — seeing one would anchor the label.')
      )
    )
  )

  const statsPanel = () => stats.bindAs(s => !s
    ? div({ class: 'text-sm opacity-60' }, 'No stats loaded.')
    : div({ class: 'card bg-base-200 border border-base-300 p-4 space-y-2' },
      h2({ class: 'font-bold' }, `Gate vs you — ${s.total} labelled`),
      div({ class: 'flex gap-6 text-sm' },
        div({}, span({ class: 'font-bold text-error' }, s.false_pass), span({ class: 'opacity-70' }, ' junk the gate let through')),
        div({}, span({ class: 'font-bold text-warning' }, s.false_reject), span({ class: 'opacity-70' }, ' good work the gate binned'))
      ),
      div({ class: 'overflow-x-auto' },
        div({ class: 'text-xs font-mono whitespace-pre' },
          s.matrix.map(m => `you:${m.human.padEnd(7)} gate:${String(m.gate).padEnd(7)} n=${String(m.n).padEnd(5)} avg score ${m.avg_score ?? '-'}`).join('\n')
        )
      )
    )
  )

  return div({ class: 'p-6 max-w-4xl mx-auto space-y-4' },
    h1({ class: 'text-2xl font-bold' }, 'Quality Gate Labelling'),
    p({ class: 'text-sm opacity-70' },
      'Judge each piece on its own merits: would you put this in the shop? The gate\'s opinion is deliberately hidden until you have labelled enough to check it against.'),
    div({ class: 'tabs tabs-boxed w-fit' }, typeTab('IMAGE', 'Artwork'), typeTab('MOCKUP', 'Mockups')),
    // Say plainly which artwork is in scope. Labelling apparel for a shop that no
    // longer sells shirts is wasted effort, and the queue quietly following the
    // Autopilot setting would be a surprise if it were not stated.
    div({ class: 'flex items-center gap-3 text-xs' },
      scope.bindAs(sc => span({ class: 'opacity-70' },
        sc === null
          ? 'Labelling every product type.'
          : `Labelling ${sc.join(' + ')} only — the types Autopilot is set to generate.`)),
      allTypes.bindAs(all => button({
        class: 'btn btn-ghost btn-xs',
        onclick: async () => { allTypes(!all); await load() }
      }, all ? 'Scope to what Autopilot generates' : 'Include every product type'))
    ),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : idx.bindAs(i => (items().length === 0 || i >= items().length)
        ? div({ class: 'card bg-base-200 border border-base-300 p-6 text-center space-y-3' },
          p({}, items().length === 0 ? 'Nothing left to label in this batch.' : 'Batch done — nice work.'),
          div({ class: 'flex gap-2 justify-center' },
            button({ class: 'btn btn-primary btn-sm', onclick: load }, 'Load another batch'),
            button({ class: 'btn btn-sm', onclick: loadStats }, 'Show gate vs you')
          )
        )
        : reviewer()
      )
    ),
    statsPanel()
  )
}
